<template>
  <div>
    <!-- Loading State -->
    <div v-if="isLoading">
      <CanPayLoader/>
    </div>
    <div class="gift-card-purchase">
      <!-- Purchase Form -->
      <div v-if="!isLoading" class="purchase-form">
        <div class="form-header">
          <div class="gift-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="30" viewBox="0 0 85 78" fill="none">
            <path d="M74.1884 16.394H68.0659C68.8381 14.8583 69.2741 13.1754 69.2741 11.4796C69.2741 6.10648 65.3426 0.66806 57.8328 0.66806C50.0966 0.66806 45.3819 6.3426 42.7366 11.3203C40.0913 6.3426 35.3766 0.66806 27.6403 0.66806C20.1306 0.66806 16.1991 6.10648 16.1991 11.4796C16.1991 13.1754 16.6351 14.8583 17.4073 16.394H11.2847C5.32223 16.394 0.473145 21.2431 0.473145 27.2056V66.5204C0.473145 72.4829 5.32223 77.332 11.2847 77.332H74.1884C80.1509 77.332 85 72.4829 85 66.5204V27.2056C85 21.2431 80.1509 16.394 74.1884 16.394ZM57.8328 6.56528C61.9256 6.56528 63.3769 9.21251 63.3769 11.4796C63.3769 13.7468 61.9256 16.394 57.8328 16.394H46.8535C48.5119 12.4056 51.9388 6.56528 57.8328 6.56528ZM27.6403 6.56528C33.4967 6.56528 36.9346 12.4042 38.6066 16.394H27.6403C23.5475 16.394 22.0963 13.7468 22.0963 11.4796C22.0963 9.21251 23.5475 6.56528 27.6403 6.56528ZM79.1027 66.5204C79.1027 69.2309 76.8989 71.4347 74.1884 71.4347H11.2847C8.57416 71.4347 6.37037 69.2309 6.37037 66.5204V61.606H79.1028L79.1027 66.5204ZM79.1027 55.7088H6.37037V27.2056C6.37037 24.495 8.57416 22.2912 11.2847 22.2912H36.8403L28.5848 33.2986C27.6058 34.602 27.8707 36.4507 29.1722 37.4278C30.4775 38.4049 32.3281 38.1342 33.2994 36.8384L42.7365 24.2569L52.1736 36.8384C52.7495 37.6101 53.6364 38.0171 54.5348 38.0171C55.1491 38.0171 55.7711 37.8251 56.3009 37.4278C57.6024 36.4506 57.8674 34.602 56.8883 33.2986L48.6328 22.2912H74.1884C76.8989 22.2912 79.1027 24.495 79.1027 27.2056V55.7088Z" fill="white"/>
            </svg>
          </div>
          <h2 class="form-title gift-card-font-weight-500">Purchase a Gift Card</h2>
        </div>

        <!-- Desktop Layout Wrapper -->
        <div class="desktop-content">
          <!-- Left Column -->
          <div class="left-column">
            <!-- Gift Card Type Selection -->
            <div class="gift-card-type-section">
              <div class="gift-card-type-title gift-card-font-weight-500">Gift Card Type</div>
              <div class="gift-card-option gift-card-type-header first-option" @click="selectGiftCardType('canpay')">
                <div class="radio-button" :class="{ checked: selectedGiftCardType === 'canpay' }"></div>
                <label for="canpay"> <span class="gift-card-font-weight-500">CanPay </span><span class="label-subtitle gift-card-font-weight-100">(Use Anywhere)</span></label>
              </div>
              <div class="gift-card-option gift-card-type-header" @click="selectGiftCardType('merchant')">
                <div class="radio-button" :class="{ checked: selectedGiftCardType === 'merchant' }"></div>
                <label for="merchant"><span class="gift-card-font-weight-500">Merchant</span> <span class="label-subtitle gift-card-font-weight-100">(Select a Merchant)</span></label>
              </div>

              <!-- Merchant Search Box - appears when merchant is selected -->
              <div v-if="selectedGiftCardType === 'merchant'" class="merchant-search-section">
                <div class="search-input-wrapper">
                  <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    class="merchant-search-input"
                    placeholder="Search Merchant"
                    v-model="merchantSearchTerm"
                    @input="onMerchantSearch"
                    @focus="showDropdown = true"
                    @blur="hideDropdownDelayed"
                  />

                  <!-- Loading state -->
                  <div v-if="showDropdown && isLoadingMerchants" class="merchant-dropdown">
                    <div class="merchant-dropdown-loading">
                      <div class="loading-spinner"></div>
                      Loading merchants...
                    </div>
                  </div>

                  <!-- Dropdown with merchant options -->
                  <div v-else-if="showDropdown && filteredMerchants.length > 0" class="merchant-dropdown">
                    <div
                      v-for="merchant in filteredMerchants"
                      :key="merchant.id"
                      class="merchant-dropdown-item"
                      :class="{ selected: selectedMerchant?.id === merchant.id }"
                      @mousedown="selectMerchant(merchant)"
                    >
                      <div class="merchant-name">{{ merchant.store_name }}</div>
                      <div class="merchant-address">{{ merchant.address }}</div>
                    </div>
                  </div>

                  <!-- No results message -->
                  <div v-else-if="showDropdown && merchantSearchTerm && filteredMerchants.length === 0 && !isLoadingMerchants" class="merchant-dropdown">
                    <div class="merchant-dropdown-no-results">
                      {{ merchantSearchTerm.length < 3 
                        ? 'Please enter at least 3 characters' 
                        : `No merchants found matching "${merchantSearchTerm}"` }}

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="right-column">
            <!-- Who is this for Section -->
            <div class="gift-card-type-section">
              <div class="gift-card-type-title gift-card-font-weight-500" >Who is this for</div>
              <div class="gift-card-option gift-card-type-header first-option" @click="selectRecipient('myself')">
                <div class="radio-button" :class="{ checked: recipient === 'myself' }"></div>
                <label for="myself"><span class="gift-card-font-weight-500">For Myself</span></label>
              </div>
              <div class="gift-card-option gift-card-type-header" @click="selectRecipient('someone-else')">
                <div class="radio-button" :class="{ checked: recipient === 'someone-else' }"></div>
                <label for="someone-else"><span class="gift-card-font-weight-500">For Someone Else</span></label>
              </div>
                <div v-if="enableEditButton && recipient === 'someone-else'">
                <button
                  
                  class="edit-button"
                  @click.stop="openEditForm"
                  title="Edit recipient details"
                >
                   Sending to {{ giftCardFormData.recipientName }}
                </button>
                </div>
            </div>
          </div>
          <!-- Amount Selection - Full Width Section -->
          <div class="purchase-amount-section">
            <div class="gift-card-type-title gift-card-font-weight-500 ">Choose an amount or enter a custom value</div>
            <div class="amount-grid" v-if="giftCardDenomination.length>0">
              <div class="amount-option" v-for="denomination in giftCardDenomination" :key="denomination.id" :class="{ selected: selectedAmount === denomination.amount && !customAmount }" @click="selectAmount(denomination.amount)">
                <span class="amount-value">${{ parseInt(denomination.amount) }}</span>
              </div>
            </div>
            <div class="custom-amount-section">
              <label class="custom-amount-label gift-card-font-weight-500">Or, enter a Custom Amount</label>
              <div class="custom-amount-input">
                <input
                  type="text"
                  v-model="displayAmount"
                  placeholder="Max $250"
                  class="amount-input"
                  @keypress="validateAmountInput($event)"
                  @input="handleAmountInput($event)"
                  @paste="handleAmountPaste($event)"
                  @blur="formatAmount"
                  ref="amountInput"
                />
              </div>
            </div>
              <div v-if="amountValidationMessage" class="amount-validation-message">
                {{ amountValidationMessage }}
              </div>
          </div>

          <div class="disclaimer gift-card-font-weight-500">
            <p>Gift cards are redeemable at participating merchants.</p>
          </div>

          <!-- Continue Button -->
          <div class="continue-section">
            <button class="continue-btn" @click="continuePurchase">
              Purchase
            </button>
          </div>
        </div>
      </div>
    </div>
    <gift-card-success ref="GiftCardSuccessModal" :textMessage="successMessage" @buy-more="goToBuyMore" @go-home="goToHome"></gift-card-success>
    <gift-card-error ref="GiftCardErrorModal" :textMessage="errorMessage"></gift-card-error>
    <gift-card-share-modal ref="GiftCardShareModal" @share-completed="onShareCompleted" :minDateProp="minDate" :getSharingData="getGiftCardSharingData" :amountOptionsProp="giftCardDenomination"></gift-card-share-modal>
    <gift-card-confirmation-modal ref="GiftCardConfirmationModal" @confirm-purchase="processPurchase"></gift-card-confirmation-modal>
    <gift-card-coming-soon-modal ref="GiftCardComingSoonModal"></gift-card-coming-soon-modal>
</div>
</template>

<script>
import api from "../../api/giftcard.js";
import paymentApi from "../../api/payment.js";
import CanPayLoader from "../CustomLoader/CanPayLoader.vue";
import GiftCardError from "./Modal/GiftCardError.vue";
import GiftCardSuccess from "./Modal/GiftCardSuccess.vue";
import GiftCardShareModal from "./Modal/GiftCardShareModal.vue";
import GiftCardConfirmationModal from "./Modal/GiftCardConfirmationModal.vue";
import GiftCardComingSoonModal from "./Modal/GiftCardComingSoonModal.vue";
export default {
  name: 'GiftCardPurchase',
  components: {
    CanPayLoader,
    GiftCardError,
    GiftCardSuccess,
    GiftCardShareModal,
    GiftCardConfirmationModal,
    GiftCardComingSoonModal
  },
  data() {
    return {
      isLoading: false,
      selectedAmount: '',
      customAmount: '',
      selectedGiftCardType: 'canpay', // 'canpay' or 'merchant'
      recipient: 'myself', // 'myself' or 'someone-else'
      merchantSearchTerm: '', // for merchant search functionality
      selectedMerchant: null, // selected merchant object
      showDropdown: false, // control dropdown visibility
      isLoadingMerchants: false, // loading state for future API calls
      merchant:[],
      giftCardDenomination:[],
      searchResults: [], // Store API search results
      searchTimeout: null, // For debouncing search requests
      errorMessage: '',
      successMessage: 'Gift card purchased successfully!',
      minDate:null,
      enableEditButton:false,
      amountValidationMessage: '', // For real-time amount validation feedback
      giftCardFormData: {
        recipientName: '',
        recipientMobile: '',
        recipientEmail: '',
        giftMessage: 'Enjoy the gift card!',
        selectedMessage: '',
        deliveryDate: ''
      },
      isDisabled: process.env.VUE_APP_GIFT_CARD_PURCHASE_DISABLED == "1"
    }
  },
  mounted() {
    // Set the header type to use PayDashboardHeader (with logo, menu icon, and reward wheel)
    this.$root.$emit("changeWhiteBackground", [false, false, "PayDashboardHeader"]);
    let self = this;
    self.fetchGiftCardDenomination();
    if(self.isDisabled){
        self.showComingSoonModal();
        return;
    }
  },
  beforeDestroy() {
    // Clear timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  watch:{
     recipient(newValue, oldValue){
       if(newValue == 'myself'){
        this.enableEditButton = false;
       }
     }
  },
  computed: {
    filteredMerchants() {
      // Return empty array when there's no search term
      if (!this.merchantSearchTerm) {
        return [];
      }

      // Only display results from the API search results
      return this.searchResults;
    },
    displayAmount: {
      get() {
        // Return the value with dollar sign for display
        if (this.customAmount !== '' && this.customAmount !== null && this.customAmount !== undefined) {
          // Always return as string with dollar sign to preserve decimal formatting
          return '$' + this.customAmount;
        }
        return '';
      },
      set(value) {
        // Clean the input value and store as string to preserve decimal formatting
        const cleanedValue = value.replace(/[^0-9.]/g, '');

        if (cleanedValue === '') {
          this.customAmount = '';
        } else {
          // Store as string to preserve decimal places like "25.50"
          this.customAmount = cleanedValue;
        }
      }
    }
  },
  methods: {
    getGiftCardSharingData(data){
      console.log('🎯 Received sharing data from modal:', data);
      this.enableEditButton = true;
      this.giftCardFormData = data;

      // If the modal provided a giftCardAmount, update the parent's amount selection
      if (data.giftCardAmount) {
        console.log('💰 Updating amount from modal:', data.giftCardAmount);
        // Check if it matches a preset amount
        const presetAmount = this.giftCardDenomination.find(d => parseFloat(d.amount) === data.giftCardAmount);
        if (presetAmount) {
          this.selectedAmount = presetAmount.amount;
          this.customAmount = '';
        } else {
          // It's a custom amount
          this.customAmount = data.giftCardAmount.toString();
          this.selectedAmount = '';
        }
      }

      // After receiving the sharing data, continue with the purchase flow
      // by showing the confirmation modal
      this.continueWithConfirmation();
    },

    continueWithConfirmation() {
      console.log('🎯 Continuing with confirmation modal after receiving recipient details');

      const amount = (this.customAmount ? parseFloat(this.customAmount) : 0) || this.selectedAmount;
      const purchaseData = {
        amount: Number(amount),
        type: this.selectedGiftCardType,
        merchant: this.selectedMerchant
      };

      const sharingData = this.recipient === 'someone-else' ? this.giftCardFormData : null;

      console.log('Showing confirmation modal with:');
      console.log('Purchase data:', purchaseData);
      console.log('Recipient:', this.recipient);
      console.log('Sharing data:', sharingData);

      this.$nextTick(() => {
        if (this.$refs['GiftCardConfirmationModal']) {
          this.$refs['GiftCardConfirmationModal'].showModal(
            purchaseData,
            this.recipient,
            sharingData
          );
        } else {
          console.error('GiftCardConfirmationModal ref not found');
        }
      });
    },
    fetchTransactionDate(){
      let self = this;
      api.getTransactionDate()
        .then((response) => {
          self.isLoading = false;
          self.minDate = response.data;
        })
        .catch((err) => {
        self.isLoading = false;
          console.error('API Error:', err);
        })
    },
    searchStore(){
      let self = this;

      // Don't search if term is too short
      if (!self.merchantSearchTerm || self.merchantSearchTerm.trim().length < 3) {
        self.searchResults = [];
        return;
      }

      self.isLoadingMerchants = true;
      const payload = {
        search: self.merchantSearchTerm.trim()
      };

      api.searchStore(payload)
        .then((response) => {
          // Handle successful response
          if (response.data && Array.isArray(response.data)) {
            self.searchResults = response.data;
          } else {
            self.searchResults = [];
          }
          self.isLoadingMerchants = false;
        })
        .catch((err) => {
          console.error('Store search API Error:', err);
          self.searchResults = [];
          self.isLoadingMerchants = false;
          self.errorMessage = 'Failed to search merchants. Please try again.';
          this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        });
    },
    fetchGiftCardDenomination(){
      let self = this;
      self.isLoading = true;
      const payload = {
        type: 'consumer'
      }
      api.fetchGiftCardDenomination(payload)
        .then((response) => {
          self.fetchTransactionDate()
          self.giftCardDenomination = response.data;
          // Default-select the first denomination box on load
          if (Array.isArray(response.data) && response.data.length > 0) {
            self.selectedAmount = '';
            self.customAmount = '';
          }
        })
        .catch((err) => {
          console.error('API Error:', err);
          self.isLoading = false;
        })
    },
    selectAmount(amount) {
      // Check if amount exceeds purchasepower
      const purchasePower = this.getAvailablePurchasePower();
      if (purchasePower !== null && amount > purchasePower) {
        this.amountValidationMessage = `Amount cannot exceed your purchase power of $${purchasePower.toFixed(2)}`;
        return; // Don't select this amount
      }

      this.selectedAmount = amount;
      this.customAmount = '';
      this.amountValidationMessage = ''; // Clear validation message when selecting preset amount
    },

    formatAmount() {
      // ✅ Format to two decimals when input is done
      if (this.customAmount && !isNaN(this.customAmount)) {
        this.customAmount = parseFloat(this.customAmount).toFixed(2);
      }
    },

    validateAmountInput(evt) {
      let value = evt.target.value;
      // Remove invalid characters
      value = value.replace(/[^0-9.]/g, '');

      // Only one dot allowed
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts[1];
      }

      // Restrict decimals to 2
      if (parts[1] && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }

      // Restrict max value
      const num = parseFloat(value);
      if (!isNaN(num) && num > 250) {
        value = '250.00';
        this.errorMessage = 'Maximum gift card amount is $250';
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
      } else {
        this.amountValidationMessage = '';
      }
      evt.target.value = value;
      this.displayAmount = '$'+value;
    },

    handleAmountInput(evt) {
      let value = evt.target.value;

      // Clean non-numeric characters except the decimal point
      let cleanedValue = value.replace(/[^0-9.]/g, '');

      // ✅ Allow only one decimal point
      const parts = cleanedValue.split('.');
      if (parts.length > 2) {
        cleanedValue = parts[0] + '.' + parts.slice(1).join('');
      }

      // ✅ Restrict to max 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        parts[1] = parts[1].slice(0, 2);
        cleanedValue = parts.join('.');
      }

      const amount = parseFloat(cleanedValue);

      // Clear validation message initially
      this.amountValidationMessage = '';

      // Update the underlying value
      if (cleanedValue === '') {
        this.customAmount = '';
      } else if (!isNaN(amount)) {
        // Get purchasepower from localStorage
        const purchasePower = this.getAvailablePurchasePower();

        // Check if amount exceeds purchasepower
        if (purchasePower !== null && amount > purchasePower) {
          this.amountValidationMessage = `Amount cannot exceed your purchase power of $${purchasePower.toFixed(2)}`;
          this.customAmount = cleanedValue;
        }
        // Check if amount exceeds 250
        else if (amount > 250) {
          this.customAmount = '250.00';
          this.errorMessage = 'Maximum gift card amount is $250';
          this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        } else {
          // Store cleaned value (already restricted to 2 decimals)
          this.customAmount = cleanedValue;
        }
      }

      // Clear selected amount when typing custom amount
      if (cleanedValue) {
        this.selectedAmount = '';
      }

      // Force cursor to end of input after formatting
      this.$nextTick(() => {
        if (this.$refs.amountInput) {
          const input = this.$refs.amountInput;
          const cursorPos = input.value.length;
          input.setSelectionRange(cursorPos, cursorPos);
        }
      });
    },

    handleAmountPaste(evt) {
      // Prevent default paste behavior
      evt.preventDefault();

      // Get pasted data with better browser compatibility
      let pastedData = '';
      if (evt.clipboardData && evt.clipboardData.getData) {
        pastedData = evt.clipboardData.getData('text');
      } else if (window.clipboardData && window.clipboardData.getData) {
        pastedData = window.clipboardData.getData('Text');
      }

      // Clean the pasted data (remove non-numeric characters except decimal point)
      const cleanedData = pastedData.replace(/[^0-9.]/g, '');

      // Check if it's a valid number
      const amount = parseFloat(cleanedData);

      if (isNaN(amount)) {
        this.amountValidationMessage = 'Please enter a valid number';
        return;
      }

      // Check constraints
      if (amount > 250) {
        this.customAmount = '250.00';
        this.amountValidationMessage = 'Maximum gift card amount is $250';
      } else if (amount < 0.01) {
        this.customAmount = parseFloat(cleanedData).toFixed(2);
        this.amountValidationMessage = 'Minimum gift card amount is $0.01';
      } else {
        // Ensure proper decimal formatting (max 2 decimal places) but preserve as string
        const parts = cleanedData.split('.');
        if (parts[1] && parts[1].length > 2) {
          this.customAmount = parts[0] + '.' + parts[1].substring(0, 2);
        } else {
          this.customAmount = parseFloat(cleanedData).toFixed(2);
        }
        this.amountValidationMessage = '';
      }
      console.log(this.customAmount)
      // Clear selected amount when pasting custom amount
      this.selectedAmount = '';
    },

    isNumber(evt) {
      // Keep the original method for backward compatibility
      evt = evt || window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      const value = this.customAmount ? this.customAmount.toString() : "";

      // Disallow first character as dot
      if (value.length === 0 && charCode === 46) {
        evt.preventDefault();
        return false;
      }

      // Only allow numbers and a single dot
      if ((charCode < 48 || charCode > 57) && (charCode !== 46 || value.indexOf('.') !== -1)) {
        evt.preventDefault();
        return false;
      }

      // Restrict to 2 decimal places
      if (value.includes('.')) {
        const [, decimalPart] = value.split('.');
        if (decimalPart.length >= 2) {
          evt.preventDefault();
          return false;
        }
      }
      this.selectedAmount = ''
      return true;
    },

    selectGiftCardType(type) {
      this.selectedGiftCardType = type;
    },
    selectRecipient(recipient) {
      console.log('🔍 selectRecipient called with:', recipient);
      console.log('📱 Device info:', {
        userAgent: navigator.userAgent,
        isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight
      });

      this.recipient = recipient;

      // If selecting "someone-else", immediately show the share modal
      if (recipient === 'someone-else') {
        console.log('👤 Someone else selected, starting validation...');

        // Validate required fields first
        const isValid = this.validatePurchaseData();
        console.log('✅ Validation result:', isValid);

        if (!isValid) {
          console.log('❌ Validation failed, resetting to myself');
          // Reset recipient selection if validation fails
          this.recipient = 'myself';
          return;
        }

        // Prepare purchase data and show modal
        const amount = this.selectedAmount || (this.customAmount ? parseFloat(this.customAmount) : 0);
        const purchaseData = {
          amount,
          type: this.selectedGiftCardType,
          merchant: this.selectedMerchant,
          giftCardFormData: this.giftCardFormData
        };

        if (this.$refs['GiftCardShareModal']) {
          console.log('🚀 Calling showModal...');
          this.$refs['GiftCardShareModal'].showModal(purchaseData);
        } else {
          console.error('❌ GiftCardShareModal ref not found!');
        }
      }
    },

    validatePurchaseData() {
      // Check if amount is selected or entered
      // const amount = this.selectedAmount || (this.customAmount ? parseFloat(this.customAmount) : 0);
      // if (!amount || amount <= 0) {
      //   this.errorMessage = 'Please select or enter a valid gift card amount.';
      //   this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
      //   return false;
      // }

      // Check if merchant is selected for merchant gift cards
      if (this.selectedGiftCardType === 'merchant' && !this.selectedMerchant) {
        this.errorMessage = 'Please select a merchant for the gift card.';
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return false;
      }

      return true;
    },

    onMerchantSearch() {
      // Handle merchant search functionality
      // Show dropdown when user starts typing
      this.showDropdown = true;

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // If search term is empty, clear search results
      if (!this.merchantSearchTerm.trim()) {
        this.searchResults = [];
        return;
      }

      // Debounce the API call to avoid too many requests
      this.searchTimeout = setTimeout(() => {
        this.searchStore();
      }, 300); // Wait 300ms after user stops typing
    },
    selectMerchant(merchant) {
      // Set the selected merchant and update search input
      this.selectedMerchant = merchant;
      this.merchantSearchTerm = merchant.store_name || merchant.name;
      this.showDropdown = false;
      this.searchResults = []; // Clear search results
      console.log('Selected merchant:', merchant);
    },
    hideDropdownDelayed() {
      // Add a small delay to allow for click events on dropdown items
      setTimeout(() => {
        this.showDropdown = false;
      }, 150);
    },

    continuePurchase() {
      const amount = (this.customAmount ? parseFloat(this.customAmount) : 0) || this.selectedAmount;
      let pp_amount = this.getAvailablePurchasePower();
      console.log(pp_amount);
      if(amount < 0.01){
        this.errorMessage = 'Minimum gift card amount is $0.01';
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return;
      }
      if (amount > 250) {
        this.errorMessage = 'Maximum gift card amount is $250';
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return;
      }
      if(amount >= pp_amount){
        this.errorMessage = `Amount cannot exceed your purchase power of $${pp_amount.toFixed(2)}`;
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return;
      }
      if (!amount || amount <= 0) {
        this.errorMessage = 'Please select or enter a valid amount';
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return;
      }

      // Check if merchant is required but not selected
      if (this.selectedGiftCardType === 'merchant' && !this.selectedMerchant) {
        this.errorMessage = 'Please select a merchant';
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return;
      }

      // If recipient is "someone-else", validate recipient details first
      if (this.recipient === 'someone-else') {
        console.log('🎯 Validating recipient details before purchase...');

        // Check if recipient details are filled
        const name = (this.giftCardFormData.recipientName || '').trim();
        const phone = this.cleanPhoneNumber(this.giftCardFormData.recipientMobile);

        if (!name || !phone) {
          console.log('❌ Recipient details missing, showing share modal for input');

          // Show the share modal to collect recipient details
          const purchaseData = {
            amount: Number(amount),
            type: this.selectedGiftCardType,
            merchant: this.selectedMerchant,
            giftCardFormData: this.giftCardFormData
          };

          if (this.$refs['GiftCardShareModal']) {
            this.$refs['GiftCardShareModal'].showModal(purchaseData);
            return; // Stop here and wait for user to fill the form
          } else {
            this.errorMessage = 'Unable to open recipient details form. Please try again.';
            this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
            return;
          }
        }

        console.log('✅ Recipient details validated, proceeding to confirmation');
      }

      // Show confirmation modal instead of directly processing purchase
      const purchaseData = {
        amount: Number(amount),
        type: this.selectedGiftCardType,
        merchant: this.selectedMerchant
      };

      const sharingData = this.recipient === 'someone-else' ? this.giftCardFormData : null;

      console.log('Attempting to show confirmation modal');
      console.log('Purchase data:', purchaseData);
      console.log('Recipient:', this.recipient);
      console.log('Sharing data:', sharingData);
      console.log('Modal ref:', this.$refs['GiftCardConfirmationModal']);

      this.$nextTick(() => {
        if (this.$refs['GiftCardConfirmationModal']) {
          this.$refs['GiftCardConfirmationModal'].showModal(
            purchaseData,
            this.recipient,
            sharingData
          );
        } else {
          console.error('GiftCardConfirmationModal ref not found');
        }
      });
    },

    processPurchase() {
      const amount = (this.customAmount ? parseFloat(this.customAmount) : 0) || this.selectedAmount;

      // Final validation before processing
      const available = this.getAvailablePurchasePower();
      const amt = Number(amount);
      var new_purchase_power = available;
      if (available !== null && isFinite(available) && amt > available) {
        this.errorMessage = `Insufficient purchase power. Your available balance is ${this.formatCurrency(available)}`;
        this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        return;
      } else {
        new_purchase_power = available - amt;
      }

      // Prepare payload for API call
      let payload = this.selectedGiftCardType === 'merchant'
        ? { amount: amt, merchant_id: this.selectedMerchant ? this.selectedMerchant.id : '', recipient: this.recipient, delivery_date: this.minDate }
        : { amount: amt, recipient: this.recipient, delivery_date: this.minDate };

      if (this.recipient === 'someone-else') {
        console.log("Gift Card Form");
        payload = this.selectedGiftCardType === 'merchant'
          ? {
              amount: amt,
              merchant_id: this.selectedMerchant ? this.selectedMerchant.id : '',
              recipient: 'someone-else',
              to_name: this.giftCardFormData.recipientName == ''? null: this.giftCardFormData.recipientName,
              type:'consumer',
              to_phone: this.giftCardFormData.recipientMobile == ''? null : this.cleanPhoneNumber(this.giftCardFormData.recipientMobile),
              to_email: this.giftCardFormData.recipientEmail == ''? null : this.giftCardFormData.recipientEmail,
              message: this.giftCardFormData.giftMessage,
              delivery_date: this.giftCardFormData.deliveryDate == '' ? null: this.giftCardFormData.deliveryDate
            }
          : {
              amount: amt,
              recipient: 'someone-else',
              to_name: this.giftCardFormData.recipientName == ''? null: this.giftCardFormData.recipientName,
              to_phone: this.giftCardFormData.recipientMobile == ''? null : this.cleanPhoneNumber(this.giftCardFormData.recipientMobile),
              type:'consumer',
              to_email: this.giftCardFormData.recipientEmail == ''? null : this.giftCardFormData.recipientEmail,
              message: this.giftCardFormData.giftMessage,
              delivery_date: this.giftCardFormData.deliveryDate == '' ? null: this.giftCardFormData.deliveryDate
            };
      }

      console.log(payload);
      this.isLoading = true;
      api.purchaseGiftCard(payload)
        .then((response) => {
          this.isLoading = false;
          if (response.code === 200) {
            this.successMessage = 'Gift Card Purchase Successful!';
            this.$refs['GiftCardSuccessModal'] && this.$refs['GiftCardSuccessModal'].showSuccessModal();
            localStorage.setItem("purchasepower", "$" + new_purchase_power);
            setTimeout(()=>{
              this.$router.push('/my-gift-cards')
            },3010)
          } else {
            this.errorMessage = 'Gift Card purchase Failed.';
            this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
          }
        })
        .catch((err) => {
          this.isLoading = false;
          const msg = (err && err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Something went wrong. Please try again.';
          this.errorMessage = msg;
          this.$refs['GiftCardErrorModal'] && this.$refs['GiftCardErrorModal'].showErrorModal();
        });
    },
    
    getPurchasePower: function() {
      var self = this;
      paymentApi
        .getPurchasePower()
        .then(function(response) {
          if (response.code == 200) {
            localStorage.setItem("purchasepower", "$" + reponse_data);
          } else if (response.code == 401) {
            this.$router.push("/login");
          } else {
          }
        })
        .catch(function(error) {
        });
    },
    cleanPhoneNumber(phone) {
      if (!phone) return '';
      return phone.replace(/[()\s-]/g, '');
    },
    // Helpers
    getAvailablePurchasePower() {
      try {
         // First try to get from localStorage 'purchasepower'
        let pp = localStorage.getItem('purchasepower');
        if (pp) {
          // Remove $ if present and convert to float
          const ppValue = parseFloat(pp.replace(/[^0-9.]/g, ''));
          if (!isNaN(ppValue)) {
            return ppValue;
          }
        }
        // Fallback: try to get from consumer_login_response
        const raw = localStorage.getItem('consumer_login_response');
        if (raw) {
          const data = JSON.parse(raw);
          const candidates = [
            data && data.purchasepower,
            data && data.purchase_power,
            data && data.purchase_power_amount,
            data && data.current_purchase_power,
            data && data.available_balance,
            data && data.balance
          ];
          for (let v of candidates) {
            if (v !== undefined && v !== null) {
              if (typeof v === 'number') return Number(v);
              if (typeof v === 'string') {
                const n = parseFloat(v.replace(/[^0-9.]/g, ''));
                if (!isNaN(n)) return n;
              }
            }
          }
        }
      } catch (e) {}
      const pp = localStorage.getItem('purchasepower');
      if (pp) {
        const n = parseFloat(pp.replace(/[^0-9.]/g, ''));
        if (!isNaN(n)) return n;
      }
      return null;
    },
    formatCurrency(val) {
      const num = Number(val);
      if (!isFinite(num)) return '$0.00';
      return `$${num.toFixed(2)}`;
    },
    goToBuyMore() {
      this.$router.push('/giftcard-purchase');
    },
    goToHome() {
      this.$router.push('/dashboard');
    },

    onShareCompleted() {
      // Handle when gift card sharing is completed
      this.successMessage = 'Gift card purchased and shared successfully!';
      this.$refs['GiftCardSuccessModal'] && this.$refs['GiftCardSuccessModal'].showSuccessModal();
      setTimeout(()=>{
        this.$router.push('/my-gift-cards')
      },3010)
    },

    openEditForm() {
      // Open the share modal to edit recipient details
      if (!this.validatePurchaseData()) {
        return;
      }

      const amount = this.selectedAmount || (this.customAmount ? parseFloat(this.customAmount) : 0);
      const purchaseData = {
        amount,
        type: this.selectedGiftCardType,
        merchant: this.selectedMerchant,
        // Pass current amount to modal so it can pre-fill
        currentAmount: amount,
        giftCardFormData: this.giftCardFormData
      };

      this.$refs['GiftCardShareModal'] && this.$refs['GiftCardShareModal'].showModal(purchaseData);
    },

    showComingSoonModal() {
      // Show the coming soon modal
      if (this.$refs.GiftCardComingSoonModal) {
        this.$refs.GiftCardComingSoonModal.showModal();
      }
    },

  }
}
</script>

<style lang="scss">
.gift-card-font-weight-500{
  font-weight:500!important;
} 
.gift-card-font-weight-100{
  font-weight:100!important;
} 
.gift-card-font-size-14{
  font-size:14px;
}
.edit-button{
  background-color: #146830;
  color: #FFFFFF;
  font-family:'Open Sans'; 
  border:none;
  width:100%;
  padding: 15px 20px;
  font-weight: 600;
  border-radius:8px;
  outline: 1px solid #FFFFFF;
  width:90%;
  margin-bottom:22px;
  text-decoration:underline;
}
// .edit-button{
//       background-color: #19793900;
//     color: #000068;
//     font-family: "Open Sans";
//     border: none;
//     width: 100%;
//     /* padding: 15px 20px; */
//     font-weight: 600;
//     border-radius: 8px;
//     /* outline: 1px solid #000068; */
//     width: 90%;
//     margin-bottom: 15px;
//     text-decoration: underline;
// }
.amount-input::placeholder {
  font-weight:500;
  color:#ffffff;
  font-size: 18px;
  text-align:center;
}

.amount-validation-message {
  margin: 20px 20px 0;
  color: #ff4444;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
  text-align: center;
  padding: 5px;
  background-color: rgba(255, 68, 68, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(255, 68, 68, 0.3);
}
</style>
